import { useAuth } from "@clerk/clerk-react";
import { Grid, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { VAStatus, VaOrganizationProfileStatus } from "schemas/dashboard";
import { routes } from "schemas/routes";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import StyledVaPage from "components/Styled/VAPage";
import QuestionBox from "components/Styled/VAPage/QuestionBox";
import { useAccountReviewState } from "features/Auth/Onboarding/shared/AccountReviewUpsell";
import { fetcherAuth } from "utils/api";
import { useTracking } from "utils/useTracking";

interface Props {
  finishSetUpLater: () => void;
}

export enum SendOption {
  now = "now",
  later = "later",
}

const SelfServeVaReview = ({ finishSetUpLater }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { trackVaStep } = useTracking();

  const { currentOrg, setCurrentOrg, setOrganizationVaProfile } = useContext(
    OrganizationUserContext,
  );

  const initialSendOption =
    (location.state as { sendOption?: SendOption } | null)?.sendOption ?? null;

  const [loading, setLoading] = useState(false);
  const [sendOption, setSendOption] = useState<SendOption | null>(
    initialSendOption,
  );
  const [error, setError] = useState<string>("");

  const { canSubmitReview } = useAccountReviewState();

  const approveOrganizationProfile = async () => {
    if (currentOrg?.vaStatus === VAStatus.approved) {
      navigate(`/${routes.virtualAssistant}`);
      return;
    }

    if (!sendOption) {
      setError("Please select when to send your emails");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/approve`,
        "POST",
        {},
        {
          sendOption,
        },
      );
      setCurrentOrg((prev) => {
        if (prev) {
          prev.vaStatus = VAStatus.approved;
        }
        return prev;
      });
      setOrganizationVaProfile((prev) => {
        if (prev) {
          prev.status =
            sendOption === SendOption.now
              ? VaOrganizationProfileStatus.completed
              : VaOrganizationProfileStatus.later;
        }
        return prev;
      });
      navigate(`/${routes.virtualAssistant}`);
    } catch (error) {
      if (error?.message?.includes("follow up question")) {
        setAlert("Please answer follow-up questions for your niches", "error");
        navigate(`/${routes.nichesFollowUpSelfServeOnboarding}`);
        return;
      }
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (!sendOption) {
      setAlert("Please select when to send your emails", "error");
      return;
    }

    if (canSubmitReview) {
      trackVaStep(routes.reviewMyAccountSelfServeOnboarding);
      navigate(`/${routes.reviewMyAccountSelfServeOnboarding}`, {
        state: { sendOption },
      });
      return;
    }

    approveOrganizationProfile();
    navigate(`/${routes.virtualAssistant}${location.search}`);
  };

  return (
    <>
      <StyledVaPage
        title="Complete Onboarding"
        description=""
        time="1"
        body={
          <QuestionBox error={error}>
            <Typography variant="h6" textAlign={"center"}>
              Would you like pitches to be sent immediately, or would you prefer
              to schedule them later? *
            </Typography>
            <Typography></Typography>
            <Typography color="textSecondary" paragraph textAlign={"center"}>
              Prefer to do this later? You can start anytime from your dashboard
            </Typography>
            <Grid container gap={1} justifyContent={"center"}>
              <StyledLargeButton
                onClick={() => {
                  setError("");
                  setSendOption(SendOption.now);
                }}
                styles={{
                  backgroundColor:
                    sendOption === SendOption.now ? "primary.main" : "#F1F2EF",
                  color: sendOption === SendOption.now ? "white" : "green.main",
                  border: "none",
                  "&:hover": {
                    backgroundColor: "yellow.main",
                    color: "green.main",
                  },
                }}
              >
                Start Immediately
              </StyledLargeButton>

              <StyledLargeButton
                onClick={() => {
                  setError("");
                  setSendOption(SendOption.later);
                }}
                styles={{
                  backgroundColor:
                    sendOption === SendOption.later
                      ? "primary.main"
                      : "#F1F2EF",
                  color:
                    sendOption === SendOption.later ? "white" : "green.main",
                  border: "none",
                  "&:hover": {
                    backgroundColor: "yellow.main",
                    color: "green.main",
                  },
                }}
              >
                Later
              </StyledLargeButton>
            </Grid>
          </QuestionBox>
        }
        stepNumber={7}
        totalSteps={canSubmitReview ? 8 : 7}
        onBack={() => {
          navigate(`/${routes.connectGmailSelfServeOnboarding}`);
          trackVaStep(routes.connectGmailSelfServeOnboarding);
        }}
        buttonBack={"Back"}
        buttonNext={canSubmitReview ? "Next" : "Done"}
        onNext={handleNext}
        onFinishSetUpLater={finishSetUpLater}
        loading={loading}
        disabledNext={!sendOption}
      />
    </>
  );
};

export default SelfServeVaReview;
